import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageTemplate from '../components/PageTemplate';

const HelpCenterPage = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const helpCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: '🚀',
      articles: [
        { id: 'gs-1', title: 'Creating Your Account' },
        { id: 'gs-2', title: 'Navigating the Dashboard' },
        { id: 'gs-3', title: 'Filling Out Your Health Form' },
        { id: 'gs-4', title: 'Understanding the AI Chat' },
        { id: 'gs-5', title: 'Downloading Your Report' }
      ]
    },
    {
      id: 'your-data',
      title: 'Your Data',
      icon: '🔒',
      articles: [
        { id: 'yd-1', title: 'Where Your Data Is Stored (On Your Device)' },
        { id: 'yd-2', title: 'What Is Sent to the AI Models' },
        { id: 'yd-3', title: 'Clearing Your Session History' },
        { id: 'yd-4', title: 'Why There Is No Account or Password' }
      ]
    },
    {
      id: 'using-the-platform',
      title: 'Using the Platform',
      icon: '💻',
      articles: [
        { id: 'up-1', title: 'Saving and Resuming Sessions' },
        { id: 'up-2', title: 'Editing Your Health Information' },
        { id: 'up-3', title: 'Sharing Reports with Providers' },
        { id: 'up-4', title: 'Using Patients Empowered on Mobile Devices' },
        { id: 'up-5', title: 'Accessing Previous Reports' }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: '🔧',
      articles: [
        { id: 'ts-1', title: 'PDF Generation Issues' },
        { id: 'ts-2', title: 'Login Problems' },
        { id: 'ts-3', title: 'Browser Compatibility' },
        { id: 'ts-4', title: 'Chat Not Responding' },
        { id: 'ts-5', title: 'Form Submission Errors' }
      ]
    },
    {
      id: 'privacy-security',
      title: 'Privacy & Security',
      icon: '🔒',
      articles: [
        { id: 'ps-1', title: 'How We Protect Your Data' },
        { id: 'ps-2', title: 'Understanding Our Privacy Policy' },
        { id: 'ps-3', title: 'Managing Your Data' },
        { id: 'ps-4', title: 'Two-Factor Authentication' }
      ]
    },
    {
      id: 'billing-subscription',
      title: 'Billing & Subscription',
      icon: '💳',
      articles: [
        { id: 'bs-1', title: 'Free vs. Premium Features' },
        { id: 'bs-2', title: 'Upgrading Your Account' },
        { id: 'bs-3', title: 'Managing Payment Methods' },
        { id: 'bs-4', title: 'Cancelling Your Subscription' },
        { id: 'bs-5', title: 'Billing FAQ' }
      ]
    }
  ];

  const popularArticles = [
    { id: 'pa-1', title: 'How to Prepare for Your First Doctor Visit', category: 'Getting Started' },
    { id: 'pa-2', title: 'Understanding Your PDF Report', category: 'Using the Platform' },
    { id: 'pa-3', title: 'Troubleshooting PDF Download Issues', category: 'Troubleshooting' },
    { id: 'pa-4', title: 'How to Share Your Report with Your Doctor', category: 'Using the Platform' }
  ];

  const recentArticles = [
    { id: 'ra-1', title: 'Using Patients Empowered Coach with Telehealth Appointments', category: 'Using the Platform', date: 'April 1, 2025' },
    { id: 'ra-2', title: 'New Feature: Patients Empowered Practice Integration', category: 'Provider Tools', date: 'March 28, 2025' },
    { id: 'ra-3', title: 'How to Use the Multi-Model AI Architecture', category: 'Using the Platform', date: 'March 15, 2025' }
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    console.log('Searching for:', searchQuery);
    // In a real application, this would trigger a search API call
  };

  return (
    <PageTemplate title="Help Center">
      <section className="mb-5">
        <div className="card text-center p-5">
          <h2 className="mb-3">How can we help you today?</h2>
          <form onSubmit={handleSearch} className="mb-0">
            <div className="flex gap-sm" style={{ maxWidth: '600px', margin: '0 auto' }}>
              <input
                type="text"
                placeholder="Search for help articles..."
                className="form-control"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="btn btn-primary">Search</button>
            </div>
          </form>
        </div>
      </section>

      <section className="mb-5">
        <h2 className="mb-4">Browse Help Categories</h2>
        <div className="grid grid-cols-1 grid-md-cols-2 grid-lg-cols-3 gap-md">
          {helpCategories.map(category => (
            <div key={category.id} className="card">
              <div className="flex gap-md">
                <div className="text-center" style={{ fontSize: '2.5rem', minWidth: '60px' }}>
                  {category.icon}
                </div>
                <div>
                  <h3 className="mb-2">{category.title}</h3>
                  <p className="mb-3 text-muted">{category.articles.length} articles</p>
                  <Link to={`/help/${category.id}`} className="btn btn-secondary btn-sm">View Articles</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 grid-lg-cols-2 gap-lg mb-5">
        <section>
          <h2 className="mb-4">Popular Articles</h2>
          <div className="card">
            {popularArticles.map((article, index) => (
              <div key={article.id} className={index < popularArticles.length - 1 ? 'mb-4 pb-4 border-bottom' : ''}>
                <h3 className="mb-1">
                  <Link to={`/help/article/${article.id}`}>{article.title}</Link>
                </h3>
                <div className="flex justify-between text-sm text-muted">
                  <span>{article.category}</span>
                  {/* <span>{article.views} views</span> */}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-4">Recently Added</h2>
          <div className="card">
            {recentArticles.map((article, index) => (
              <div key={article.id} className={index < recentArticles.length - 1 ? 'mb-4 pb-4 border-bottom' : ''}>
                <h3 className="mb-1">
                  <Link to={`/help/article/${article.id}`}>{article.title}</Link>
                </h3>
                <div className="flex justify-between text-sm text-muted">
                  <span>{article.category}</span>
                  <span>Added: {article.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="text-center p-5 card">
        <h2 className="mb-3">Can't find what you're looking for?</h2>
        <p className="mb-4">Our support team is ready to assist you with any questions or issues.</p>
        <div className="flex justify-center gap-md">
          <Link to="/contact" className="btn btn-primary">Contact Support</Link>
          <Link to="/faq" className="btn btn-secondary">View FAQ</Link>
        </div>
      </section>
    </PageTemplate>
  );
};

export default HelpCenterPage;
